import {
    Button,
    DeprecatedCustomDownIcon as CustomDown,
    DeprecatedCustomUpIcon as CustomUp,
    Flex,
    IconButton,
    Link,
    Menu,
    MenuItem,
    MenuList,
    Modal,
} from '@lokalise/louis';
import { useState } from 'react';

import type { DesignPlugins } from '@app/api/team';
import { useActivateDesignPluginsTrialMutation } from '@app/api/team';
import { showUnexpectedErrorToast } from '@app/components/Toast';
import useInterval from '@app/hooks/useInterval';
import useSelector from '@app/hooks/useSelector';
import { getProject } from '@app/selectors';
import track, { AnalyticsEventName } from '@app/utils/analytics';
import { navigateToPath } from '@app/utils/location';

import designReviewGif from '../assets/static/design-review.gif';
import ThumbDown from './ThumbDown';
import ThumbUp from './ThumbUp';
import { ContentWrapper, CtaButton, FeedbackContent, FeedbackWrapper, ListItem, ReviewList } from './styles';

// These times come from the Adobe Premiere Gif editor
const reviewAnimationTiming = {
    // frame: duration
    1: 2450, // 00:00-02:45
    2: 4850, // 02:45-07:30
    3: 1700, // 07:30-09:00
    4: 3000, // 09:00-12:00
} as const;

type TryDesignPluginsProps = {
    teamId: string;
};

const appsPluginCode = {
    figma: `app:figma`,
    adobe: `app:adobe-xd`,
    sketch: `app:sketch-plugin`,
};

const TryDesignPlugins = ({ teamId }: TryDesignPluginsProps) => {
    const [reviewStep, setReviewStep] = useState<keyof typeof reviewAnimationTiming | null>(null);
    const [isNotInterested, setIsNotInterested] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isEnablingPlugin, setIsEnablingPlugin] = useState(false);
    const [isUpdatingNotInterested, setIsUpdatingNotInterested] = useState(false);
    const { id: projectId } = useSelector(getProject);
    const [enableDesignPluginsTrial] = useActivateDesignPluginsTrialMutation();
    const isUpdateInProgress = isUpdatingNotInterested || isEnablingPlugin;

    const startReviewAnimation = () => setReviewStep(1);

    // Once the review animation is started, it keeps looping over
    // it forever (to match the gif frames on the right side).
    useInterval(
        () => {
            switch (reviewStep) {
                case 1:
                    setReviewStep(2);
                    break;

                case 2:
                    setReviewStep(3);
                    break;

                case 3:
                    setReviewStep(4);
                    break;

                case 4:
                    setReviewStep(1);
                    break;

                default:
                    break;
            }
        },
        reviewStep === null ? null : reviewAnimationTiming[reviewStep],
    );

    const enablePlugin = async (designPluginName: DesignPlugins) => {
        setIsEnablingPlugin(true);
        const response = await enableDesignPluginsTrial({
            teamId,
            status: 'activated',
            designPluginName,
        });

        if ('error' in response) {
            showUnexpectedErrorToast();

            return;
        }

        const pluginCode = appsPluginCode[designPluginName];
        const installPluginUrl = `/apps/${projectId}/#${pluginCode}`;
        const isAlreadyInAppsPage = window.location.pathname.startsWith('/apps');

        navigateToPath(installPluginUrl);

        if (isAlreadyInAppsPage) {
            // In other pages we want to keep the dialog open to show the loading spinner until the
            // page redirect happens, but when user is already in /apps page, we just close this dialog.
            setIsDialogOpen(false);
        }
    };

    const markAsNotInterested = async () => {
        setIsUpdatingNotInterested(true);
        const response = await enableDesignPluginsTrial({
            teamId,
            status: 'not_interested',
        });

        if ('error' in response) {
            showUnexpectedErrorToast();

            return;
        }

        setIsNotInterested(true);
    };

    const [isFeedbackVisible, setIsFeedbackVisible] = useState(true);

    const rateFeature = async (eventName: AnalyticsEventName) => {
        track(eventName);
        setIsFeedbackVisible(false);
    };

    const openDialog = () => {
        setIsDialogOpen(true);
        track(AnalyticsEventName.DESIGN_PLUGINS_BUTTON_CLICKED);
    };

    if (isNotInterested) {
        return null;
    }

    const hasTakenAction = isEnablingPlugin || isUpdatingNotInterested;

    return (
        <>
            {!hasTakenAction && (
                <CtaButton appearance="danger" onClick={openDialog}>
                    Try design plugins for free
                </CtaButton>
            )}

            <Modal
                title="Free 14 days trial of design plugins"
                onClose={() => setIsDialogOpen(false)}
                opened={isDialogOpen}
                overflow="visible"
            >
                <ContentWrapper>
                    <Flex gap={4} direction="column">
                        <div>
                            <strong>Get time back</strong> for your designers and engineers by previewing translations
                            in designs. <strong>Use real content to design</strong>.<br />
                            See{' '}
                            <Link href="https://lokalise.com/case-studies/withings" target="_blank" rel="noreferrer">
                                Withings case study
                            </Link>
                            .
                        </div>

                        <ReviewList>
                            <ListItem $isHighlighted={reviewStep === 1}>Pull in content to your design tool</ListItem>
                            <ListItem $isHighlighted={reviewStep === 2}>
                                Review translations in multiple languages
                            </ListItem>
                            <ListItem $isHighlighted={reviewStep === 3}>
                                Find issues, fix them to have pristine UI
                            </ListItem>
                            <ListItem $isHighlighted={reviewStep === 4}>Push changes back for review</ListItem>
                        </ReviewList>

                        <Flex gap={2}>
                            <Menu
                                menuButton={({ opened }) => (
                                    <Button
                                        variant="primary"
                                        rightIcon={opened ? <CustomUp /> : <CustomDown />}
                                        loading={isEnablingPlugin}
                                        disabled={isUpdateInProgress}
                                    >
                                        Try for free
                                    </Button>
                                )}
                            >
                                <MenuList>
                                    <MenuItem onClick={() => enablePlugin('figma')}>Try Figma plugin</MenuItem>
                                    <MenuItem onClick={() => enablePlugin('adobe')}>Try Adobe XD plugin</MenuItem>
                                    <MenuItem onClick={() => enablePlugin('sketch')}>Try Sketch plugin</MenuItem>
                                </MenuList>
                            </Menu>

                            <Button
                                variant="secondary"
                                onClick={markAsNotInterested}
                                loading={isUpdatingNotInterested}
                                disabled={isUpdateInProgress}
                            >
                                Not interested
                            </Button>
                        </Flex>
                    </Flex>

                    <img
                        src={designReviewGif}
                        alt="Design review animation"
                        height={200}
                        width={232}
                        onLoad={startReviewAnimation}
                    />
                </ContentWrapper>

                {isFeedbackVisible && (
                    <FeedbackWrapper>
                        <FeedbackContent>
                            <span>Did you like the way we told you about this feature?</span>
                            <Flex gap={2}>
                                <IconButton
                                    ariaLabel="Yes, I like this."
                                    appearance="primary"
                                    onClick={() => rateFeature(AnalyticsEventName.DESIGN_PLUGINS_AD_LIKED)}
                                >
                                    <ThumbUp />
                                </IconButton>
                                <IconButton
                                    ariaLabel="No, I don't like this."
                                    appearance="primary"
                                    onClick={() => rateFeature(AnalyticsEventName.DESIGN_PLUGINS_AD_DISLIKED)}
                                >
                                    <ThumbDown />
                                </IconButton>
                            </Flex>
                        </FeedbackContent>
                    </FeedbackWrapper>
                )}
            </Modal>
        </>
    );
};

export default TryDesignPlugins;
